// frontend/src/pages/BookingSuccessPage.tsx
// ✅ FIXED: Corrected status comparison and CSS conflicts

import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { 
  CheckCircle, 
  Calendar, 
  MapPin, 
  Phone, 
  Mail, 
  Clock,
  AlertCircle,
  ArrowLeft,
  Download,
  Share2,
  MessageSquare
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useBookingDetails } from '@/hooks/useBookings';

// Define proper booking status type
type BookingStatus = 'pending' | 'accepted' | 'rejected' | 'completed' | 'cancelled';

const BookingSuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const bookingId = searchParams.get('booking_id');
  const requestId = searchParams.get('request_id');

  // State
  const [copied, setCopied] = useState(false);

  // Fetch booking details
  const {
    data: booking,
    isLoading,
    error
  } = useBookingDetails(bookingId || '');

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { 
        state: { 
          from: { pathname: `/booking-success?booking_id=${bookingId}&request_id=${requestId}` }
        }
      });
    }
  }, [isAuthenticated, navigate, bookingId, requestId]);

  // Handle share booking
  const handleShare = async () => {
    const shareData = {
      title: 'Booking Confirmation',
      text: `Booking confirmed for ${booking?.service_name}`,
      url: window.location.href
    };

    if (navigator.share) {
      try {
        await navigator.share(shareData);
      } catch (err) {
        console.log('Error sharing:', err);
        handleCopyLink();
      }
    } else {
      handleCopyLink();
    }
  };

  // Handle copy link
  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Get status color and icon
  const getStatusDisplay = (status: BookingStatus) => {
    switch (status) {
      case 'pending':
        return {
          color: 'bg-yellow-100 text-yellow-800',
          icon: Clock,
          message: 'Your booking request is pending provider confirmation'
        };
      case 'accepted':
        return {
          color: 'bg-green-100 text-green-800',
          icon: CheckCircle,
          message: 'Your booking has been confirmed by the provider'
        };
      case 'rejected':
        return {
          color: 'bg-red-100 text-red-800',
          icon: AlertCircle,
          message: 'Your booking request was declined'
        };
      case 'completed':
        return {
          color: 'bg-blue-100 text-blue-800',
          icon: CheckCircle,
          message: 'Your booking has been completed'
        };
      case 'cancelled':
        return {
          color: 'bg-gray-100 text-gray-800',
          icon: AlertCircle,
          message: 'Your booking has been cancelled'
        };
      default:
        return {
          color: 'bg-gray-100 text-gray-800',
          icon: Clock,
          message: 'Booking status unknown'
        };
    }
  };

  // Format date and time
  const formatDateTime = (date?: string, time?: string) => {
    if (!date) return 'Not scheduled yet';
    
    const dateObj = new Date(date);
    const dateStr = dateObj.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    return time ? `${dateStr} at ${time}` : dateStr;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading booking details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || !booking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Booking Not Found
          </h1>
          <p className="text-gray-600 mb-6">
            {error?.message || 'The booking you\'re looking for could not be found.'}
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-md flex items-center mx-auto"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const statusDisplay = getStatusDisplay(booking.status);
  const StatusIcon = statusDisplay.icon;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center text-gray-600 hover:text-gray-900 mr-4"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  Booking Confirmation
                </h1>
                <p className="text-sm text-gray-600">
                  Booking ID: {booking.booking_id}
                </p>
              </div>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={handleShare}
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md flex items-center"
              >
                <Share2 className="h-4 w-4 mr-2" />
                {copied ? 'Copied!' : 'Share'}
              </button>
              <button
                onClick={() => window.print()}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center"
              >
                <Download className="h-4 w-4 mr-2" />
                Download
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Status Card */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center mb-4">
                <div className={`p-3 rounded-full ${statusDisplay.color.replace('text-', 'bg-').replace('bg-', 'bg-opacity-20 bg-')} mr-4`}>
                  <StatusIcon className={`h-6 w-6 ${statusDisplay.color.split(' ')[1]}`} />
                </div>
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    {/* ✅ FIXED: Removed invalid status comparison */}
                    {booking.status === 'accepted' ? 'Booking Confirmed!' : 
                     booking.status === 'pending' ? 'Booking Request Submitted!' :
                     'Booking Update'}
                  </h2>
                  <p className="text-gray-600">{statusDisplay.message}</p>
                </div>
              </div>
              
              <div className={`inline-flex px-3 py-1 text-sm font-medium rounded-full ${statusDisplay.color}`}>
                Status: {booking.status.charAt(0).toUpperCase() + booking.status.slice(1)}
              </div>
            </div>

            {/* Service Details */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Service Details
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start">
                  <Calendar className="h-5 w-5 text-gray-400 mr-3 mt-1" />
                  <div>
                    <p className="font-medium text-gray-900">{booking.service_name}</p>
                    <p className="text-sm text-gray-600">
                      {(booking as any).provider_business_name || (booking as any).provider_name || 'Service Provider'}
                    </p>
                  </div>
                </div>

                {(booking as any).scheduled_date && (
                  <div className="flex items-start">
                    <Clock className="h-5 w-5 text-gray-400 mr-3 mt-1" />
                    <div>
                      <p className="font-medium text-gray-900">Scheduled Time</p>
                      <p className="text-sm text-gray-600">
                        {formatDateTime((booking as any).scheduled_date, (booking as any).scheduled_time)}
                      </p>
                    </div>
                  </div>
                )}

                {(booking as any).service_address && (
                  <div className="flex items-start">
                    <MapPin className="h-5 w-5 text-gray-400 mr-3 mt-1" />
                    <div>
                      <p className="font-medium text-gray-900">Service Location</p>
                      <p className="text-sm text-gray-600">{(booking as any).service_address}</p>
                    </div>
                  </div>
                )}

                {booking.estimated_duration && (
                  <div className="flex items-start">
                    {/* ✅ FIXED: Removed duplicate w-4 classes */}
                    <Clock className="h-5 w-5 text-gray-400 mr-3 mt-1" />
                    <div>
                      <p className="font-medium text-gray-900">Estimated Duration</p>
                      <p className="text-sm text-gray-600">
                        {booking.estimated_duration} minutes
                      </p>
                    </div>
                  </div>
                )}

                {booking.total_amount && (
                  <div className="flex items-start">
                    <div className="h-5 w-5 text-gray-400 mr-3 mt-1 flex items-center justify-center">
                      <span className="text-sm font-bold">$</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">Total Amount</p>
                      <p className="text-sm text-gray-600">
                        {(booking as any).currency || '$'}{booking.total_amount}
                      </p>
                    </div>
                  </div>
                )}

                {(booking as any).booking_notes && (
                  <div className="flex items-start">
                    <MessageSquare className="h-5 w-5 text-gray-400 mr-3 mt-1" />
                    <div>
                      <p className="font-medium text-gray-900">Special Notes</p>
                      <p className="text-sm text-gray-600">{(booking as any).booking_notes}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Customer Information */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Customer Information
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center">
                  <div className="h-5 w-5 text-gray-400 mr-3 flex items-center justify-center">
                    <span className="text-sm">👤</span>
                  </div>
                  <span className="text-sm text-gray-900">{booking.customer_name}</span>
                </div>
                
                <div className="flex items-center">
                  <Phone className="h-5 w-5 text-gray-400 mr-3" />
                  <a 
                    href={`tel:${booking.customer_phone}`}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {booking.customer_phone}
                  </a>
                </div>
                
                <div className="flex items-center">
                  <Mail className="h-5 w-5 text-gray-400 mr-3" />
                  <a 
                    href={`mailto:${booking.customer_email}`}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    {booking.customer_email}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Actions */}
          <div className="space-y-6">
            {/* Provider Contact */}
            {(booking as any).provider_contact && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Provider Contact
                </h3>
                
                <div className="space-y-3">
                  <p className="font-medium text-gray-900">
                    {booking.provider_business_name || booking.provider_name}
                  </p>
                  
                  {(booking as any).provider_contact?.phone && (
                    <div className="flex items-center">
                      <Phone className="h-4 w-4 text-gray-400 mr-2" />
                      <a 
                        href={`tel:${(booking as any).provider_contact?.phone}`}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        {(booking as any).provider_contact?.phone}
                      </a>
                    </div>
                  )}
                  
                  {(booking as any).provider_contact?.email && (
                    <div className="flex items-center">
                      <Mail className="h-4 w-4 text-gray-400 mr-2" />
                      <a 
                        href={`mailto:${(booking as any).provider_contact?.email}`}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        {(booking as any).provider_contact?.email}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Quick Actions
              </h3>
              
              <div className="space-y-3">
                <Link
                  to="/dashboard"
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white text-center py-2 px-4 rounded-md text-sm block"
                >
                  View All Bookings
                </Link>
                
                <Link
                  to="/"
                  className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 text-center py-2 px-4 rounded-md text-sm block"
                >
                  Book Another Service
                </Link>
                
                {booking.status === 'pending' && (
                  <button
                    onClick={() => navigate(`/booking/${booking.booking_id}/modify`)}
                    className="w-full bg-yellow-100 hover:bg-yellow-200 text-yellow-800 text-center py-2 px-4 rounded-md text-sm"
                  >
                    Modify Request
                  </button>
                )}
                
                {(booking.status === 'accepted' || booking.status === 'pending') && (
                  <button
                    onClick={() => navigate(`/booking/${booking.booking_id}/cancel`)}
                    className="w-full bg-red-100 hover:bg-red-200 text-red-800 text-center py-2 px-4 rounded-md text-sm"
                  >
                    Cancel Booking
                  </button>
                )}
              </div>
            </div>

            {/* Support */}
            <div className="bg-blue-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-2">
                Need Help?
              </h3>
              <p className="text-sm text-blue-700 mb-4">
                If you have any questions about your booking, we're here to help.
              </p>
              <Link
                to="/support"
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Contact Support →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingSuccessPage;